import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {createGeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode, meshGeometryAttributeSetLike} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import {meshGeometryAttributeLike, curveGeometryAttributeLike} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {MeshGeometryAttributeLikeNode, CurveGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {MeshGeometryAttributeSetLikeNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {NumberLike} from "#template/nodes/types"
import {numberLike} from "#template/nodes/number/fundamental"
import {IsNonNull} from "@cm/utils"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {Operators} from "#template/geometry-processing/geometry-graph"
import type {AttributeRefSet} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import {
    applyGeometryOperator,
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    geometryAttributeRefDataSetToGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export const CUT_OPERATOR_OUTPUT_CUT_MESH_ATTRIBUTE_SET = "CutOperator.cutMeshAttributeSet"
export const CUT_OPERATOR_OUTPUT_WINDING_ATTRIBUTE_SET = "CutOperator.windingAttributeSet"

// Single-variant operator (mesh-only): parameter type is concrete, no per-dim genericity.
export type CutOperatorParameters = {
    name: string
    meshUVAttribute: MeshGeometryAttributeLikeNode | null
    meshAttributeSet: MeshGeometryAttributeSetLikeNode | null
    curveUVAttribute: CurveGeometryAttributeLikeNode | null
    curveGroupIDAttribute: CurveGeometryAttributeLikeNode | null
    groupIDs: NumberLike[]
}

export const cutOperatorParametersSchema = z.object({
    name: z.string(),
    meshUVAttribute: z.nullable(meshGeometryAttributeLike),
    meshAttributeSet: z.nullable(meshGeometryAttributeSetLike),
    curveUVAttribute: z.nullable(curveGeometryAttributeLike),
    curveGroupIDAttribute: z.nullable(curveGeometryAttributeLike),
    groupIDs: z.array(numberLike),
})

function applyCutOperator(
    meshUVAttribute: GeometryAttributeData<2>,
    meshAttributeSet: GeometryAttributeSetData<2>,
    curveUVAttribute: GeometryAttributeData<1>,
    curveGroupIDAttribute: GeometryAttributeData<1>,
    groupIDs: number[],
): GeometryAttributeSetData<2> {
    const meshUVAttrRefData = meshUVAttribute.ref
    const meshAttrRefDataSet = geometryAttributeSetDataToGeometryAttributeRefDataSet(meshAttributeSet)
    const curveUVAttrRefData = curveUVAttribute.ref
    const curveGroupIDAttrRefData = curveGroupIDAttribute.ref

    const cutAttrRefDataSet = applyGeometryOperator(
        (meshUVAttrRef: AttributeRef, meshAttrRefSet: AttributeRefSet, curveUVAttrRef: AttributeRef, curveGroupIDAttrRef: AttributeRef, groupIDs: number[]) => {
            const [cutMeshAttrRefSet, windingAttrRefList] = Operators.cut(meshUVAttrRef, meshAttrRefSet, curveUVAttrRef, curveGroupIDAttrRef, groupIDs)

            const windingAttrRefSet: AttributeRefSet = {}
            windingAttrRefList.forEach((attrRef, idx) => {
                windingAttrRefSet[groupIDs[idx]] = attrRef
            })
            return {
                cutMeshAttrRefSet,
                windingAttrRefSet,
            }
        },
        meshUVAttrRefData,
        meshAttrRefDataSet,
        curveUVAttrRefData,
        curveGroupIDAttrRefData,
        groupIDs,
    )

    const cutMeshAttributeSet = geometryAttributeRefDataSetToGeometryAttributeSetData(cutAttrRefDataSet.cutMeshAttrRefSet, meshUVAttribute.dim)
    const windingAttributeSet = geometryAttributeRefDataSetToGeometryAttributeSetData(cutAttrRefDataSet.windingAttrRefSet, meshUVAttribute.dim)

    return createGeometryAttributeSetData(
        {
            [`${CUT_OPERATOR_OUTPUT_CUT_MESH_ATTRIBUTE_SET}`]: cutMeshAttributeSet,
            [`${CUT_OPERATOR_OUTPUT_WINDING_ATTRIBUTE_SET}`]: windingAttributeSet,
        },
        meshUVAttribute.dim,
    )
}

export abstract class GeometryCutOperatorNode<out P extends CutOperatorParameters = CutOperatorParameters> extends GeometryAttributeSetNode<2, P> {
    protected override hasValidParameters(parameters: P): parameters is P & NonNullableParameters<P> {
        return (
            parameters.meshUVAttribute !== null &&
            parameters.meshAttributeSet !== null &&
            parameters.curveUVAttribute !== null &&
            parameters.curveGroupIDAttribute !== null &&
            parameters.groupIDs.length > 0
        )
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<P>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<2> | null>; invalidator?: BuilderInlet<null>} {
        const {meshUVAttribute, meshAttributeSet, curveUVAttribute, curveGroupIDAttribute, groupIDs} = parameters

        const [meshUVAttributeDataValid, meshUVAttributeDataInvalid] = scope.branch(evaluator.evaluate(meshUVAttribute))
        const [meshAttributeSetDataValid, meshAttributeSetDataInvalid] = scope.branch(evaluator.evaluate(meshAttributeSet))
        const [curveUVAttributeDataValid, curveUVAttributeDataInvalid] = scope.branch(evaluator.evaluate(curveUVAttribute))
        const [curveGroupAttributeDataValid, curveGroupAttributeDataInvalid] = scope.branch(evaluator.evaluate(curveGroupIDAttribute))

        const [groupIDsValid, groupIDsInvalid] = scope.branch(
            scope.pureLambda(
                scope.list(groupIDs.map((groupID) => evaluator.evaluate(groupID))),
                (groupIDs) => {
                    const validGroupIDs = groupIDs.filter(IsNonNull)
                    return validGroupIDs.length > 0 ? validGroupIDs : null
                },
                "groupIDs",
            ),
        )

        const geometryAttributeSetData = scope.pureLambda(
            scope.tuple(meshUVAttributeDataValid, meshAttributeSetDataValid, curveUVAttributeDataValid, curveGroupAttributeDataValid, groupIDsValid),
            ([meshUVAttributeData, meshAttributeSetData, curveUVAttributeData, curveGroupAttributeData, groupIDs]) =>
                applyCutOperator(meshUVAttributeData, meshAttributeSetData, curveUVAttributeData, curveGroupAttributeData, groupIDs),
            "applyCutOperator",
        )

        return {
            evaluatedGeometryData: geometryAttributeSetData,
            invalidator: scope.phi(
                meshUVAttributeDataInvalid,
                meshAttributeSetDataInvalid,
                curveUVAttributeDataInvalid,
                curveGroupAttributeDataInvalid,
                groupIDsInvalid,
            ),
        }
    }
}
